import { colorsCssVariablesCore } from '@activebrands/core-web/config/branding/colors';
import { siteCssVariables } from '@activebrands/core-web/config/branding/site';
import { breakpoints } from '@activebrands/core-web/config/breakpoints';
import { colorsCssVariables } from '../colors';
import { gridCssResponsiveVariables } from '../grid';
import { opacityCssVariables } from '../opacity';
import { ratiosCssResponsiveVariables } from '../ratios';
import { transitionsCssResponsiveVariables } from '../transitions';
import { zIndexCssResponsiveVariables } from '../z-index';
import { Theme } from './index';

/**
 * When creating variables, use the color palette in color.ts when defining the values.
 * For example, '--color-text' uses 'var(--color-palette-off-black)' from the color palette.
 *
 * We don't want to use other variables as variable values as this defeats the purpose of individual variables.
 * For example, '--link-color' should not use '--color-accent-secondary', even if they have the same value,
 * as they could be different in another theme.
 *
 * Knowing when to use an already existing variable or creating a new one can be difficult.
 * A rule of thumb is to think about the colors as they could be used in different themes.
 * Maybe the link color and the accent color are both blue in the default theme, but it's possible they could be different
 * in different themes. In that case they should probably have their own variable so they can easily be modified
 * in different themes.
 *
 * Variable format:
 * --property-[where]-[component]-[state]
 * For example: color-text-button-active, height-header, etc.
 */

const theme: Theme = {
    statics: {
        // Core colors
        ...colorsCssVariablesCore,

        // Default Configs
        ...colorsCssVariables,
        ...opacityCssVariables,
        ...siteCssVariables,

        // ----------------------------//
        // Color tokens
        // ----------------------------//

        // UI
        '--color-ui-content-primary': 'var(--color-base-black)',
        '--color-ui-content-secondary': 'var(--color-base-beige-700)',
        '--color-ui-content-hover': 'var(--color-base-beige-800)',
        '--color-ui-content-disabled': 'var(--color-base-beige-600)',
        '--color-ui-content-sale': 'var(--color-base-red)',
        '--color-ui-bg-background': 'var(--color-base-white)',
        '--color-ui-bg-surface': 'var(--color-base-beige-200)',
        '--color-ui-bg-block': 'var(--color-base-beige-400)',
        '--color-ui-border-default': 'var(--color-base-beige-300)',

        // Gradients and overlays
        '--color-gradient-default-hard': 'var(--color-base-white)',
        '--color-gradient-default-soft': 'var(--color-base-white-25)',
        '--color-gradient-default-transparent': 'var(--color-base-white-0)',
        '--color-gradient-surface-hard': 'var(--color-base-beige-200)',
        '--color-gradient-surface-soft': 'var(--color-base-beige-50)',
        '--color-gradient-surface-transparent': 'var(--color-base-beige-0)',
        '--color-overlay-default': 'var(--color-base-black-25)',

        // Buttons
        '--color-btn-primary-fill-default': 'var(--color-base-black)',
        '--color-btn-primary-fill-hover': 'var(--color-base-beige-800)',
        '--color-btn-primary-fill-active': 'var(--color-base-beige-300)',
        '--color-btn-primary-fill-disabled': 'var(--color-base-beige-800)',
        '--color-btn-primary-content-default': 'var(--color-base-white)',
        '--color-btn-primary-content-hover': 'var(--color-base-white)',
        '--color-btn-primary-content-active': 'var(--color-base-black)',
        '--color-btn-primary-content-disabled': 'var(--color-base-beige-500)',
        '--color-btn-secondary-fill-default': 'var(--color-base-beige-300)',
        '--color-btn-secondary-fill-hover': 'var(--color-base-beige-200)',
        '--color-btn-secondary-fill-active': 'var(--color-base-black)',
        '--color-btn-secondary-fill-disabled': 'var(--color-base-beige-500)',
        '--color-btn-secondary-content-default': 'var(--color-base-black)',
        '--color-btn-secondary-content-hover': 'var(--color-base-black)',
        '--color-btn-secondary-content-active': 'var(--color-base-white)',
        '--color-btn-secondary-content-disabled': 'var(--color-base-beige-700)',

        // Inputs
        '--color-input-default-fill-default': 'var(--color-base-white)',
        '--color-input-default-fill-hover': 'var(--color-base-beige-200)',
        '--color-input-default-fill-active': 'var(--color-base-white)',
        '--color-input-default-fill-disabled': 'var(--color-base-beige-300)',
        '--color-input-default-label-default': 'var(--color-base-beige-700)',
        '--color-input-default-content-default': 'var(--color-base-beige-700)',
        '--color-input-default-content-hover': 'var(--color-base-beige-800)',
        '--color-input-default-content-active': 'var(--color-base-black)',
        '--color-input-default-content-disabled': 'var(--color-base-beige-700)',
        '--color-input-default-content-error': 'var(--color-base-red)',
        '--color-input-default-border-default': 'var(--color-base-beige-400)',
        '--color-input-default-border-hover': 'var(--color-base-beige-500)',
        '--color-input-default-border-active': 'var(--color-base-black)',
        '--color-input-default-border-error': 'var(--color-base-red)',

        // Badges and banners
        '--color-badge-sale-new': 'var(--color-base-black)',
        '--color-badge-sale': 'var(--color-base-red)',
        '--color-badge-article-bg': 'var(--color-base-beige-300)',
        '--color-badge-article-label': 'var(--color-base-black)',
        '--color-banner-pri-fill': 'var(--color-base-black)',
        '--color-banner-pri-content': 'var(--color-base-white)',

        // Semantic
        '--color-semantic-error': 'var(--color-base-red)',
        '--color-semantic-error-200': 'var(--color-base-red-15)',
        '--color-semantic-warning': 'var(--color-base-amber)',
        '--color-semantic-success': 'var(--color-base-green)',
        '--color-semantic-success-200': 'var(--color-base-green-15)',
        '--color-focus': 'var(--color-base-focus)',

        // ----------------------------//
        // Global General Variables
        // ---------------------------//

        '--color-outline-focus': 'var(--color-focus)',
        '--color-scrollbar-thumb': 'var(--color-base-beige-800)',
        '--color-scrollbar-track': 'var(--color-base-beige-500)',

        // TODO: Rearrange and rename core section
        // ----------------------------//
        // Core
        // ---------------------------//

        // Gradient
        '--color-bg-gradient': 'var(--color-gradient-default-transparent)',

        // Backdrop
        '--color-bg-backdrop': 'var(--color-palette-brand-brown-25)',
        '--top-content-backdrop': 'var(--dynamic-main-content-top)',
        '--transition-duration-content-backdrop': 'var(--duration-fast)',
        '--transition-timing-function-content-backdrop': 'var(--ease)',
        '--zindex-content-overlay-backdrop': 'var(--zindex-overlay)',

        // Breadcrumbs
        '--color-text-breadcrumbs-color-default': 'var(--color-ui-content-secondary)',
        '--color-text-breadcrumbs-color-active': 'var(--color-ui-content-primary)',
        '--padding-content-breadcrumbs': 'var(--margin-content)',

        // Price
        '--color-price-active-original': 'var(--color-ui-content-primary)',
        '--color-text-price-original-active': 'var(--color-ui-content-sale)',
        '--color-text-price-sale-active': 'var(--color-ui-content-secondary)',

        // Tooltip
        '--tooltip-content-background-color': 'var(--color-bg-secondary)',
        '--tooltip-content-border-color': 'var(--color-contrast-medium)',

        // ----------------------------//
        // Text
        // ---------------------------//

        '--color-text-accent': 'var(--color-semantic-error)',
        '--color-text-disabled': 'var(--color-ui-content-primary)',
        '--color-text-error': 'var(--color-semantic-error)',
        '--color-text-inverted': 'var(--color-ui-bg-background)',
        '--color-text-subtle': 'var(--color-ui-content-secondary)',
        '--color-text': 'var(--color-ui-content-primary)',
        '--color-text-pagination': 'var(--color-ui-content-primary)',
        '--color-text-pagination-subtle': 'var(--color-ui-content-secondary)',

        // ----------------------------//
        // Background
        // ----------------------------//

        '--color-bg-accent': 'var(--color-base-error)',
        '--color-bg-error': 'var(--color-base-error)',
        '--color-bg-highlight': 'var(--color-palette-white)',
        '--color-bg-inverted': 'var(--color-ui-content-primary)',
        '--color-bg-secondary': 'var(--color-ui-bg-surface)',
        '--color-bg': 'var(--color-ui-bg-background)',
        '--color-bg-contrast': 'var(--color-ui-bg-surface)',
        '--color-bg-active-navigation': 'var(--color-ui-bg-block)',
        '--color-bg-list-bullet': 'var(--color-ui-content-primary)',
        '--color-bg-beige': 'var(--color-palette-neutral-300)',
        '--color-bg-white': 'var(--color-base-brand-white)',
        '--color-bg-black': 'var(--color-base-brand-black)',
        // ----------------------------//
        // Form Information
        // ----------------------------//

        '--color-bg-form-information': 'var(--color-ui-bg-background)',
        '--color-border-form-information-error': 'var(--color-semantic-error)',
        '--color-border-form-information-success': 'var(--color-ui-content-secondary)',
        '--color-icon-form-information-error': 'var(--color-semantic-error)',
        '--color-icon-form-information-success': 'var(--color-semantic-success)',
        '--color-text-form-information': '--color-palette-off-black',

        // ----------------------------//
        // Border
        // ----------------------------//

        '--color-border': 'var(--color-ui-content-primary)',
        '--color-border-subtle': 'var(--color-ui-border-default)',
        '--color-border-inverted': 'var(--color-ui-bg-background)',
        '--color-border-accent': 'var(--color-semantic-error)',
        '--color-border-error': 'var(--color-semantic-error)',
        '--color-border-site-selector': 'var(--color-ui-content-secondary)',

        // ----------------------------//
        // Contrast
        // ----------------------------//

        '--color-contrast-lowest': 'var(--color-ui-bg-surface)',
        '--color-contrast-lower': 'var(--color-palette-neutral-300)',
        '--color-contrast-low': 'var(--color-palette-neutral-400)',
        '--color-contrast-medium': 'var(--color-palette-neutral-500)',
        '--color-contrast-high': 'var(--color-palette-neutral-600)',
        '--color-contrast-higher': 'var(--color-palette-neutral-600)',
        '--color-contrast-dark': 'var(--color-palette-black)',

        // ----------------------------//
        // Links
        // ----------------------------//

        '--link-color': 'var(--color-palette-brown)',

        '--color-link-rich-text': 'var(--color-palette-neutral-600)',
        '--color-link-rich-text-hover': 'var(--color-ui-content-primary)',

        // ----------------------------//
        // Shadow
        // ----------------------------//

        '--color-shadow': 'var(--color-palette-off-black)',
        '--color-shadow-inverted': 'var(--color-palette-off-white)',
        '--shadow-drop': '',

        // ----------------------------//
        // Buttons
        // ----------------------------//

        /** Default */
        '--color-bg-button-default': 'var(--color-palette-neutral-400)',
        '--color-text-button-default': 'var(--color-palette-off-black)',

        // Default (Hover)
        '--color-bg-button-default-hover': 'var(--color-palette-neutral-300)',
        '--color-text-button-default-hover': 'var(--color-palette-off-black)',

        // Default (Disabled)
        '--color-text-button-default-disabled': 'var(--color-ui-content-secondary)',

        // Default (Active)
        '--color-bg-button-default-active': 'var(--color-ui-content-primary)',
        '--color-text-button-default-active': 'var(--color-ui-bg-background)',
        '--color-menu-button-default-active': 'var(--color-ui-bg-block)',

        /** Inverted */
        '--color-bg-button-inverted': 'var(--color-btn-primary-fill-default)',
        '--color-text-button-inverted': 'var(--color-btn-primary-content-default)',

        // Inverted(Hover)
        '--color-bg-button-inverted-hover': 'var(--color-palette-brown)',
        '--color-text-button-inverted-hover': 'var(--color-palette-off-white)',

        // Inverted (Disabled)
        '--color-text-button-inverted-disabled': 'var(--color-palette-off-white)',
        '--color-bg-button-inverted-disabled': 'var(--color-palette-brown)',

        // Inverted (Active)
        '--color-bg-button-inverted-active': 'var(--color-palette-neutral-400)',
        '--color-text-button-inverted-active': 'var(--color-palette-off-black)',

        /** Link */
        '--color-bg-button-link': 'transparent',
        '--color-text-button-link': 'var(--color-ui-content-primary)',

        // Link (Hover)
        '--color-bg-button-link-hover': 'var(--color-palette-neutral-300)',
        '--color-text-button-link-hover': 'var(--color-palette-off-black)',

        // Link (Active)
        '--color-bg-button-link-active': 'var(--color-palette-neutral-400)',
        '--color-text-button-link-active': 'var(--color-palette-off-black)',

        // Link (Disabled)
        '--color-bg-button-link-disabled': 'var(--color-palette-brown)',
        '--color-text-button-link-disabled': 'var(--color-palette-off-white)',

        // ACT colors
        '--color-text-voucher-subtle': 'var(--color-palette-default-grey)',

        // Checkbox input
        '--color-label-checkbox-loading': 'var(--color-palette-default-grey)',
        '--color-label-checkbox-disabled': 'var(--color-palette-default-grey)',
        '--color-text-focus-checkbox-label': 'var(--color-base-black-10)',

        // Navigation buttons
        '--color-bg-navigation-buttons': 'var(--color-ui-bg-background)',
        '--color-bg-navigation-buttons-hover': 'var(--color-btn-secondary-fill-default)',
        // ----------------------------//
        // Footer
        // ----------------------------//

        '--color-bg-footer-primary': 'var(--color-ui-bg-surface)',
        '--color-bg-footer-secondary': 'var(--color-btn-secondary-fill-default)',
        '--color-bg-footer-button': 'var(--color-ui-bg-surface)',
        '--color-bg-footer-accordion': 'var(--color-ui-bg-block)',

        // ----------------------------//
        // Header
        // ----------------------------//

        '--color-bg-header': 'var(--color-ui-bg-surface)',
        '--color-bg-search-header': 'var(--color-ui-bg-background)',
        '--color-border-search-header': 'var(--color-input-default-border-default)',
        '--color-text-header': 'var(--color-ui-content-primary)',
        '--color-text-search-header': 'var(--color-ui-content-primary)',
        '--color-top-logo-mobile': 'var(--color-ui-content-primary)',

        // ----------------------------//
        // Header Overlays
        // ----------------------------//

        '--color-bg-header-overlay': 'var(--color-ui-bg-surface)',

        // ----------------------------//
        // Banner
        // ----------------------------//

        // Beige
        '--color-bg-banner-beige': 'var(--color-palette-neutral-300)',
        '--color-text-banner-beige': 'var(--color-palette-off-black)',

        // Black
        '--color-bg-banner-black': 'var(--color-palette-off-black)',
        '--color-text-banner-black': 'var(--color-palette-off-white)',

        // ----------------------------//
        // Navigation
        // ----------------------------//
        '--color-bg-navigation': 'var(--color-ui-bg-background)',
        '--color-text-navigation': 'var(--color-ui-content-primary)',

        // ----------------------------//
        // Badges
        // ----------------------------//

        // ----------------------------//
        // Swatches
        // ----------------------------//

        '--color-swatches-border-color-highlight': 'var(--color-ui-content-primary)',
        '--color-swatches-border-color-highlight-inner': 'var(--color-ui-bg-background)',
        '--color-swatches-border-color': 'var(--color-palette-neutral-500)',

        // ----------------------------//
        // Modules
        // ----------------------------//

        '--border-radius-module': '0px',

        // Default
        '--color-bg-badge': 'transparent',
        '--color-text-badge': 'var(--color-ui-content-primary)',

        // Brand primary
        '--color-bg-badge-brand-primary': 'var(--color-badge-article-bg)',
        '--color-text-badge-brand-primary': 'var(--color-badge-article-label)',

        // Accent
        '--color-bg-badge-accent': 'transparent',
        '--color-text-badge-accent': 'var(--color-ui-content-sale)',

        // ----------------------------//
        // Tags
        // ----------------------------//

        '--color-bg-article-tag': 'var(--color-palette-primary-400)',
        '--color-text-article-tag': 'var(--color-ui-content-primary)',
        '--color-bg-order-tag': 'var(--color-palette-brown)',

        // ----------------------------//
        // Preview
        // ----------------------------//

        '--color-bg-preview': 'var(--color-palette-neutral-500)',
        '--color-bg-preview-button': 'var(--color-palette-neutral-500)',

        // ----------------------------//
        // Images
        // ----------------------------//

        '--color-bg-image': 'var(--color-palette-light-grey)',

        // ----------------------------//
        // PLP
        // ----------------------------//
        '--color-text-category-button': 'var(--color-palette-neutral-600)',
        '--color-bg-category-button': 'var(--color-palette-neutral-300)',
        '--color-text-category-button-active': 'var(--color-palette-off-white)',
        '--color-bg-category-button-active': 'var(--color-palette-neutral-600)',
        '--color-text-category-button-hover': 'var(--color-palette-off-white)',
        '--color-bg-category-button-hover': 'var(--color-palette-neutral-400)',
        '--color-border-button': 'var(--color-ui-content-primary)',

        // Variant Selector
        '--color-text-variant-label': 'var(--color-ui-content-secondary)',
        '--color-border-variant-image-active': 'var(--color-ui-content-primary)',
        '--color-border-variant-image-hover': 'var(--color-ui-content-secondary)',
        '--color-bg-variant-sale-badge': 'var(--color-base-error)',
        '--color-text-variant-sale-badge': 'var(--color-palette-white)',

        // Sticky filter bar
        '--color-bg-sticky-filter-bar': 'var(--color-ui-content-primary)',
        '--color-text-sticky-filter-bar': 'var(--color-ui-bg-background)',

        // ----------------------------//
        // PDP
        // ----------------------------//

        '--color-bg-gallery-button': 'var(--color-ui-bg-background)',
        '--color-bg-gallery-placeholder': 'var(--color-palette-neutral-200)',
        '--color-bg-model-size': 'var(--color-ui-bg-background)',
        '--color-bg-pdp-flipclock': 'var(--color-palette-white)',
        '--color-bg-pdp-important-content': 'var(--color-ui-bg-surface)',
        '--color-text-pdp-usp': 'var(--color-ui-content-primary)',
        '--color-bg-gallery-badge': 'var(--color-palette-off-white)',
        '--color-bg-gallery-badge-hover': 'var(--color-palette-neutral-200)',
        '--color-text-pdp-flipclock': 'var(--color-ui-content-primary)',
        '--color-text-pdp-info': 'var(--color-ui-content-secondary)',
        '--color-text-pdp-usp-description': 'var(--color-palette-default-grey)',
        '--color-border-thumbnail': 'var(--color-base-brand-black-25)',
        '--color-border-thumbnail-active': 'var(--color-ui-content-primary)',

        // ----------------------------//
        // Checkout
        // ----------------------------//

        '--color-bg-checkout-info-boxes': 'var(--color-palette-white)',
        '--color-bg-checkout-act-content': 'var(--color-palette-white)',
        '--color-bg-checkout-act-information': 'var(--color-palette-white)',
        '--color-bg-checkout-act-vouchers-active': 'var(--color-palette-act-blue)',
        '--color-bg-checkout-act-vouchers': 'var(--color-palette-white)',
        '--color-bg-checkout-summary-products': 'var(--color-ui-bg-surface)',
        '--color-bg-checkout-summary-total': 'var(--color-ui-bg-surface)',
        '--color-bg-checkout-summary-vouchers-autofill': 'var(--color-ui-bg-surface)',
        '--color-bg-checkout': 'var(--color-ui-bg-background)',
        '--color-border-checkout-act-content': 'var(--color-ui-border-default)',
        '--color-border-checkout-act-vouchers-active': 'var(--color-palette-act-blue)',
        '--color-border-checkout-act-vouchers': 'var(--color-ui-border-default)',
        '--color-border-checkout-info-boxes': 'var(--color-ui-content-primary)',
        '--color-border-checkout-subtle': 'var(--color-ui-border-default)',
        '--color-icon-checkout-act-content': 'var(--color-palette-act-blue)',
        '--color-text-checkout-act-information': 'var(--color-palette-off-white)',
        '--color-text-checkout-act-vouchers-active': 'var(--color-palette-off-white)',
        '--color-text-checkout-act-vouchers': 'var(--color-ui-content-primary)',
        '--color-text-checkout-error': 'var(--color-semantic-error)',
        '--color-text-checkout-steps-active': 'var(--color-ui-content-primary)',
        '--color-text-checkout-steps': 'var(--color-ui-content-secondary)',
        '--color-text-checkout-subtle': 'var(--color-ui-content-secondary)',
    },
    responsives: {
        'mobile.sm': {
            '--height-overlay': 'calc(100dvh - var(--height-header) - 8px)', // -8px is for the gap between header and overlay
            '--margin-bottom-archive-hero': '64px',
            '--margin-bottom-default-hero': '40px',
            '--margin-bottom-module': '64px',
            '--margin-content': '8px',
            '--padding-content-shop-the-look': '0px',
            '--padding-image-pdp': '5%',
            '--padding-image-productCard': '10%',
            '--width-overlays': 'calc(100vw - 8px)', // -8px is 4px x 2 gap between browser edge and overlay
            '--width-basket-overlay': 'calc(100vw - 8px)', // -8px is 4px x 2 gap between browser edge and overlay
        },
        'mobile.md': {},
        'mobile.lg': {
            '--width-basket-overlay': '500px',
            '--width-overlays': '400px',
        },
        'tablet.sm': {},
        'tablet.lg': {},
        'desktop.sm': {
            '--checkout-corridor-max-width': '1440px',
            '--margin-bottom-archive-hero': '80px',
            '--margin-bottom-default-hero': '64px',
            '--margin-bottom-module': '80px',
            '--margin-content': '16px',
            '--padding-image-productCard': '5%',
        },
        'desktop.md': {
            '--padding-image-pdp': '10%',
        },
        'desktop.lg': {
            '--site-corridor-max-width': '1920px',
        },
    },
};

breakpoints.forEach(breakpoint => {
    theme.responsives[breakpoint.label] = {
        ...ratiosCssResponsiveVariables[breakpoint.label],
        ...gridCssResponsiveVariables[breakpoint.label],
        ...transitionsCssResponsiveVariables[breakpoint.label],
        ...zIndexCssResponsiveVariables[breakpoint.label],

        // Always write the theme variables last
        ...theme.responsives[breakpoint.label],
    };
});

export default theme;
